/**
 * A computer player ("bot") used to measure how hard a level is.
 *
 * It's deliberately imperfect, roughly like an average human: it usually
 * launches blocks that can clear something, but not always the best one, and
 * sometimes it makes careless mistakes. Playing a level many times and
 * counting the wins gives a "win rate": a number from 0 to 1 where higher
 * means easier.
 *
 * It plays "headless", meaning without drawing anything, on a copy of the
 * level, moving one track slot per step, so a game takes only a few
 * milliseconds. Note that it does not check whether a level CAN be won: that
 * is guaranteed by how levels are built (see level.ts).
 */
import { mulberry32 } from "./random";
import {
	exposedCounts,
	type GameState,
	launchFrom,
	planLaunch,
	type Source,
	update,
} from "./state";

/** How the bot plays: how good its choices are, how eager it is to launch, and how careless. */
export interface BotOptions {
	/**
	 * How smart its choices are: 0 picks randomly among the blocks that could
	 * clear something; 1 always picks the block that can reach the most cells.
	 */
	skill: number;
	/**
	 * Chance, on each step, of launching another block while some are already on
	 * the track. Lower is more patient.
	 */
	launchRate: number;
	/**
	 * Chance that a launch is a careless mistake: picking any launchable block,
	 * whether or not it can clear anything.
	 */
	blunder: number;
	/**
	 * Stop and count the game as lost after this many steps, so a game that can
	 * never finish doesn't run forever.
	 */
	maxSteps: number;
}

/** Settings for an average player: sensible most of the time, occasionally careless. */
export const DEFAULT_BOT: BotOptions = {
	skill: 0.5,
	launchRate: 0.15,
	blunder: 0.15,
	maxSteps: 20_000,
};

/** Every launch possible right now: one entry per block the player could click. */
function launchOptions(state: GameState): { source: Source; plan: Source[] }[] {
	const sources: Source[] = [
		...state.columns.flatMap((c, index) =>
			c.length > 0 ? [{ from: "column" as const, index }] : [],
		),
		...state.tray.flatMap((b, index) =>
			b ? [{ from: "tray" as const, index }] : [],
		),
	];
	return sources.flatMap((source) => {
		const plan = planLaunch(state, source);
		return plan ? [{ source, plan }] : [];
	});
}

/** The block at a launch source (the same idea as the helper in state.ts). */
function blockAt(state: GameState, s: Source) {
	return s.from === "column"
		? state.columns[s.index]?.[0]
		: state.tray[s.index];
}

/**
 * Decide whether to launch something this step, and if so, what.
 *
 * Each possible launch is scored by how many reachable cells match its
 * color. Usually the bot picks among the useful ones (score above 0); with
 * probability `skill` it takes the best. With probability `blunder` it just
 * picks anything.
 */
function decide(
	state: GameState,
	random: () => number,
	options: BotOptions,
): void {
	const idle = state.runners.length === 0;
	if (!idle && random() > options.launchRate) return;

	const choices = launchOptions(state);
	if (choices.length === 0) return;

	const exposed = exposedCounts(state.grid);
	const scored = choices.map((choice) => ({
		...choice,
		score: choice.plan.reduce((sum, s) => {
			const block = blockAt(state, s);
			return (
				sum + (block && block.count > 0 ? (exposed.get(block.color) ?? 0) : 0)
			);
		}, 0),
	}));
	if (random() < options.blunder) {
		const pick = scored[Math.floor(random() * scored.length)];
		if (pick) launchFrom(state, pick.source);
		return;
	}
	const useful = scored.filter((c) => c.score > 0);
	// If nothing is on the track, something has to be launched or the game can't
	// progress, even if no block can clear anything yet.
	const pool = useful.length > 0 ? useful : idle ? scored : [];
	if (pool.length === 0) return;

	const pick =
		random() < options.skill
			? pool.reduce((best, c) => (c.score > best.score ? c : best))
			: pool[Math.floor(random() * pool.length)];
	if (pick) launchFrom(state, pick.source);
}

/**
 * Play one whole game from `start` and return `true` if the bot won. The
 * level passed in is not changed: the bot plays on a deep copy made with
 * `structuredClone`.
 */
export function playout(
	start: GameState,
	seed: number,
	options: BotOptions = DEFAULT_BOT,
): boolean {
	const state = structuredClone({ ...start, events: [] });
	const random = mulberry32(seed);
	const dt = 1 / state.speed;
	for (
		let step = 0;
		step < options.maxSteps && state.status === "playing";
		step++
	) {
		decide(state, random, options);
		update(state, dt);
		state.events.length = 0; // nothing reads this copy's event log, so empty it to save memory
	}
	return state.status === "won";
}

/**
 * How often the average bot wins this level, from 0 to 1 (playing it
 * `playouts` times with different random choices). Higher means easier. This
 * measures difficulty only; it doesn't prove a level can be won.
 */
export function winRate(state: GameState, playouts = 16, seed = 1): number {
	let wins = 0;
	for (let i = 0; i < playouts; i++) {
		if (playout(state, seed * 1000 + i, DEFAULT_BOT)) wins++;
	}
	return wins / playouts;
}
